import React from "react";
import { ReceiptText, Share2 } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import Modal from "../components/Modal";
import ShareModal from "../components/ShareModal";
import { useReceipt } from "../data/ReceiptContext";
import "../components/CircleButton.scss";
import "./FixedBottomBar.scss";

interface FixedBottomBarProps {
  canEdit: boolean;
  // nudge shown left of the actions before the check is shared
  showShareHint: boolean;
  // fills the left side once shared (the selected mate's compact total)
  summary?: React.ReactNode;
  // the shareable link once created, or null if not shared yet
  existingUrl: string | null;
  // create the shared receipt and return its url (used when not shared yet)
  onCreate: () => Promise<string | null>;
}

const FixedBottomBar: React.FC<FixedBottomBarProps> = ({
  canEdit,
  showShareHint,
  summary,
  existingUrl,
  onCreate,
}) => {
  const { pictureUploaded, receipt } = useReceipt();

  const [showReceiptModal, setShowReceiptModal] = React.useState(false);
  const [showShareModal, setShowShareModal] = React.useState(false);

  // hold the receipt icon to show the photo; release anywhere hides it. We end
  // the gesture via window listeners (the modal backdrop covers the icon, so its
  // own pointerup can't be relied on) and release capture so scrolling isn't left
  // stuck.
  const handleReceiptPressStart = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* nothing captured — ignore */
    }
    setShowReceiptModal(true);
    const hide = () => {
      setShowReceiptModal(false);
      window.removeEventListener("pointerup", hide);
      window.removeEventListener("pointercancel", hide);
    };
    window.addEventListener("pointerup", hide);
    window.addEventListener("pointercancel", hide);
  };

  return (
    <>
      <div className="fixed-bottom-bar">
        {/* always rendered so it takes the free width and keeps the actions
            right-aligned */}
        <div className="bottom-bar-summary">
          {showShareHint ? (
            <div className="share-hint">
              Share the check with your mates once everything looks correct
            </div>
          ) : (
            summary
          )}
        </div>
        <div className="bottom-bar-actions">
          {pictureUploaded && (
            <div
              className="circle-button"
              style={{ userSelect: "none" }}
              onPointerDown={handleReceiptPressStart}
              onContextMenu={(e) => e.preventDefault()}
            >
              <ReceiptText />
            </div>
          )}
          {canEdit && (
            <div
              className="circle-button"
              onClick={() => setShowShareModal(true)}
            >
              <Share2 />
            </div>
          )}
        </div>
      </div>

      {/* AnimatePresence lets each modal play its close animation */}
      <AnimatePresence>
        {showReceiptModal && (
          // press-and-hold preview: vanish almost as soon as you let go
          <Modal
            key="receipt"
            bare
            handleClose={() => setShowReceiptModal(false)}
            exitDuration={0.1}
          >
            <img
              src={receipt.pictureUrl}
              alt="Receipt"
              style={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                borderRadius: "16px",
              }}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showShareModal && (
          <ShareModal
            key="share"
            existingUrl={existingUrl}
            onCreate={onCreate}
            handleClose={() => setShowShareModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FixedBottomBar;
